import React, { useMemo } from 'react';
import { adaptiveSamplingTheme, PLOTLY_PLOT_MARGIN } from './theme';

export type PlotMargin = { t: number; r: number; b: number; l: number };

export type PointOverlayProps = {
  /**
   * Points drawn on top of a HeatmapPanel. Coordinates are in the same
   * axis units as the underlying Plotly chart — the parent is responsible
   * for wiring this overlay's SVG viewBox to the heatmap's x/y ranges
   * (or for passing already-projected pixel coords).
   */
  points: Array<{
    x: number;
    y: number;
    iter: number;
    source: 'sampler' | 'manual';
    status: 'queued' | 'running' | 'done';
  }>;
  proposedBatch?: Array<{ x: number; y: number; rank: number }>;
  /** Explicit axis ranges. Defaults auto-computed from the points. */
  xRange?: [number, number];
  yRange?: [number, number];
  /** Max marker iter — used to bin the colour gradient. */
  maxIter?: number;
  /**
   * Pixel margins of the Plotly plotting rect within the chart container.
   * FXC-9003: the overlay used to cover the full container, ignoring these
   * margins, so points were shifted off the heatmap. Defaults to the shared
   * adaptive-sampling base layout margin so callers stay aligned by default.
   */
  plotMargin?: PlotMargin;
};

/**
 * Project an axis-unit point into the overlay's [0,100] viewBox space.
 * SVG y is flipped (0 at top). Exported for unit testing the coordinate map.
 */
export function projectToViewBox(
  x: number,
  y: number,
  ranges: { xMin: number; xMax: number; yMin: number; yMax: number },
): { cx: number; cy: number } {
  const { xMin, xMax, yMin, yMax } = ranges;
  const cx = xMax === xMin ? 50 : ((x - xMin) / (xMax - xMin)) * 100;
  const cy = yMax === yMin ? 50 : 100 - ((y - yMin) / (yMax - yMin)) * 100;
  return { cx, cy };
}

/**
 * SVG overlay showing sampler picks, proposed next-batch candidates, and
 * queued/manual points on top of a heatmap or blank canvas. Markers follow
 * the adaptive-sampling theme:
 *   - Sampler done / running → diamond, colour by iter (sequential palette).
 *   - Queued               → hollow circle with pulse ring.
 *   - Manual                → filled square with "M" badge.
 *   - Proposed next batch   → hollow diamond with rank numeral, cyan pulse.
 */
export const PointOverlay: React.FC<PointOverlayProps> = ({
  points,
  proposedBatch,
  xRange,
  yRange,
  maxIter,
  plotMargin = PLOTLY_PLOT_MARGIN,
}) => {
  const t = adaptiveSamplingTheme;

  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    const allX = [
      ...points.map((p) => p.x),
      ...(proposedBatch?.map((p) => p.x) ?? []),
    ];
    const allY = [
      ...points.map((p) => p.y),
      ...(proposedBatch?.map((p) => p.y) ?? []),
    ];
    const [autoXMin, autoXMax] =
      allX.length > 0 ? [Math.min(...allX), Math.max(...allX)] : [0, 1];
    const [autoYMin, autoYMax] =
      allY.length > 0 ? [Math.min(...allY), Math.max(...allY)] : [0, 1];
    return {
      xMin: xRange?.[0] ?? autoXMin,
      xMax: xRange?.[1] ?? autoXMax,
      yMin: yRange?.[0] ?? autoYMin,
      yMax: yRange?.[1] ?? autoYMax,
    };
  }, [points, proposedBatch, xRange, yRange]);

  const effectiveMaxIter = useMemo(
    () => maxIter ?? Math.max(1, ...points.map((p) => p.iter)),
    [points, maxIter],
  );

  // Map [xMin, xMax] → [0, 100], [yMin, yMax] → [0, 100] (SVG y flipped).
  const projX = (x: number) => projectToViewBox(x, 0, { xMin, xMax, yMin, yMax }).cx;
  const projY = (y: number) => projectToViewBox(0, y, { xMin, xMax, yMin, yMax }).cy;

  /** Pick colour from sequential palette by iter index. */
  const iterColor = (iter: number) => {
    const p = t.sequentialPalette;
    if (p.length === 0) return t.accent;
    const idx = Math.min(
      p.length - 1,
      Math.max(0, Math.round((iter / effectiveMaxIter) * (p.length - 1))),
    );
    return p[idx] ?? t.accent;
  };

  return (
    <svg
      className="as-point-overlay"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      // FXC-9003: inset the overlay to the Plotly plotting rect (container minus
      // axis margins) so sample points line up with the heatmap beneath them.
      // The CSS sets `inset: 0`; override per-edge with the plot margins.
      style={{
        top: plotMargin.t,
        right: plotMargin.r,
        bottom: plotMargin.b,
        left: plotMargin.l,
        width: 'auto',
        height: 'auto',
      }}
    >
      {/* Sampler diamonds (done / running). */}
      {points
        .filter((p) => p.source === 'sampler' && p.status !== 'queued')
        .map((p, i) => {
          const cx = projX(p.x);
          const cy = projY(p.y);
          const c = iterColor(p.iter);
          const r = 1.6;
          const pts = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
          return (
            <polygon
              key={`s-${i}`}
              points={pts}
              fill={c}
              stroke={t.background}
              strokeWidth={0.3}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

      {/* Queued sampler points — hollow circle with pulse. */}
      {points
        .filter((p) => p.status === 'queued')
        .map((p, i) => (
          <circle
            key={`q-${i}`}
            cx={projX(p.x)}
            cy={projY(p.y)}
            r={1.6}
            fill="none"
            stroke={t.textMuted}
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
          >
            <animate
              attributeName="r"
              values="1.6;2.6;1.6"
              dur="1.6s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="1;0.35;1"
              dur="1.6s"
              repeatCount="indefinite"
            />
          </circle>
        ))}

      {/* Manual picks — filled square with "M" badge. */}
      {points
        .filter((p) => p.source === 'manual')
        .map((p, i) => {
          const cx = projX(p.x);
          const cy = projY(p.y);
          const r = 1.8;
          return (
            <g key={`m-${i}`}>
              <rect
                x={cx - r}
                y={cy - r}
                width={r * 2}
                height={r * 2}
                fill={t.pink}
                stroke={t.background}
                strokeWidth={0.3}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={cx}
                y={cy + 0.7}
                className="as-manual-badge"
                textAnchor="middle"
              >
                M
              </text>
            </g>
          );
        })}

      {/* Proposed next-batch candidates — hollow diamond with rank numeral. */}
      {proposedBatch?.map((p, i) => {
        const cx = projX(p.x);
        const cy = projY(p.y);
        const r = 2.1;
        const pts = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
        return (
          <g key={`p-${i}`}>
            <polygon
              points={pts}
              fill="none"
              stroke={t.accent}
              strokeWidth={0.6}
              vectorEffect="non-scaling-stroke"
            >
              <animate
                attributeName="stroke-opacity"
                values="1;0.3;1"
                dur="1.8s"
                repeatCount="indefinite"
              />
            </polygon>
            <text
              x={cx}
              y={cy + 0.6}
              fontSize={1.8}
              fill={t.accent}
              textAnchor="middle"
              fontFamily="JetBrains Mono, monospace"
            >
              {p.rank}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
